import { Alert, Button, Form, Input, Modal, Select, Space } from "@arco-design/web-react";
import { useEffect, useRef, useState } from "react";
import { isHttpClientError } from "../../../lib/http/errors";
import { getProvinceOptions, normalizeProvinceName } from "../../../shared/region";
import type { PointCandidate, PointCandidateResult, PointCandidateQuery } from "../types";
import type { MainDataCreateRequest, MainDataKind, MainDataResult } from "../types/mainData";
import { ItineraryDialogCloseButton } from "./ItineraryDialogCloseButton";

type Props = {
  kind: MainDataKind;
  defaultProvince?: string;
  defaultStayArea?: { id: string; name: string };
  disabled?: boolean;
  onCreate: (request: MainDataCreateRequest) => Promise<MainDataResult>;
  onListCandidates?: (query: PointCandidateQuery, signal?: AbortSignal) => Promise<PointCandidateResult>;
  onDirtyChange?: (dirty: boolean) => void;
  onClose: () => void;
};

/** 行程编辑器内新增主数据并直接显示成功或失败结果。 */
export function ItineraryMainDataDialog({ kind, defaultProvince = "", defaultStayArea,
  disabled = false, onCreate, onListCandidates, onDirtyChange, onClose }: Props) {
  const [province, setProvince] = useState(defaultProvince);
  const [name, setName] = useState("");
  const [stayAreaId, setStayAreaId] = useState(defaultStayArea?.id ?? "");
  const [keyword, setKeyword] = useState(defaultStayArea?.name ?? "");
  const [candidates, setCandidates] = useState<PointCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [searchError, setSearchError] = useState("");
  const [result, setResult] = useState<MainDataResult>();
  const busy = useRef(false);
  const searchVersion = useRef(0);
  const searchControllerRef = useRef<AbortController | null>(null);
  const label = kind === "point" ? "考察点" : "住宿点";
  const created = result?.outcome === "created";
  const frozen = disabled || pending || created;

  useEffect(() => {
    onDirtyChange?.(!created && Boolean(name.trim() || province !== defaultProvince));
    return () => onDirtyChange?.(false);
  }, [created, defaultProvince, name, onDirtyChange, province]);

  useEffect(() => {
    if (kind !== "point" || !onListCandidates) return;
    const version = ++searchVersion.current;
    const controller = new AbortController();
    searchControllerRef.current = controller;
    const timer = setTimeout(() => {
      if (controller.signal.aborted) return;
      setSearching(true);
      void onListCandidates({ kind: "stay_area", keyword: keyword.trim() || undefined,
        provinceNames: province ? [province] : undefined,
        ...(!keyword.trim() ? { page: 1, pageSize: 20 } : {}) }, controller.signal)
        .then((page) => {
          if (controller.signal.aborted || searchVersion.current !== version) return;
          setCandidates(page.items);
          setSearchError("");
          const selected = page.items.find((item) => item.refId === stayAreaId);
          if (!province && selected?.city) setProvince(normalizeProvinceName(selected.city) ?? "");
        }).catch(() => {
          if (!controller.signal.aborted && searchVersion.current === version) setSearchError("住宿区域加载失败，请重新搜索");
        }).finally(() => {
          if (!controller.signal.aborted && searchVersion.current === version) setSearching(false);
        });
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); searchVersion.current += 1; };
  }, [kind, keyword, onListCandidates, province, stayAreaId]);

  const clearResult = () => { setError(""); setResult(undefined); };
  const close = () => {
    if (busy.current) return;
    searchControllerRef.current?.abort();
    setSearching(false);
    onClose();
  };
  const submit = async () => {
    if (busy.current || disabled || created) return;
    if (!province || !name.trim() || (kind === "point" && !stayAreaId)) {
      setError(`请填写省份、${label}名称${kind === "point" ? "并选择住宿区域" : ""}`);
      return;
    }
    busy.current = true;
    setPending(true);
    setError("");
    try {
      setResult(await onCreate({ kind, name: name.trim(), province,
        stayAreaId: kind === "point" ? stayAreaId : null }));
    } catch (failure) {
      setError(isHttpClientError(failure) && failure.code?.startsWith("ITINERARY_AI_")
        ? "判重服务暂不可用，请稍后重试；本次未新增。"
          : failure instanceof Error ? failure.message : "请求失败，请检查后重新提交");
    } finally {
      busy.current = false;
      setPending(false);
    }
  };
  const options = candidates.map((item) => ({ value: item.refId, label: item.name }));
  if (defaultStayArea && stayAreaId === defaultStayArea.id
    && !options.some((item) => item.value === defaultStayArea.id)) {
    options.unshift({ value: defaultStayArea.id, label: defaultStayArea.name });
  }

  return <Modal visible title={`添加${label}`} style={{ width: 540, maxWidth: "calc(100vw - 32px)" }}
    closeIcon={<ItineraryDialogCloseButton label={`关闭添加${label}`} />}
    maskClosable={false} escToExit={!pending} closable={!pending}
    onCancel={close}
    footer={<Space>
      <Button disabled={pending} onClick={close}>{created ? "完成" : "取消"}</Button>
      {!created && <Button type="primary" loading={pending}
        disabled={disabled || pending} onClick={() => void submit()}>
        提交
      </Button>}
    </Space>}>
    <Form layout="vertical">
      <Form.Item label="省份" required>
        <Select aria-label="省份" showSearch value={province || undefined} placeholder="请选择省份"
          options={getProvinceOptions()} disabled={frozen}
          onChange={(value) => { clearResult(); setProvince(value); setStayAreaId(""); setKeyword(""); setCandidates([]); }} />
      </Form.Item>
      {kind === "point" && <Form.Item label="住宿区域" required>
        <Select aria-label="住宿区域" showSearch filterOption={false} options={options}
          value={stayAreaId || undefined} placeholder="搜索考察点所属住宿区域" loading={searching}
          disabled={frozen} onSearch={setKeyword}
          onChange={(value) => { searchControllerRef.current?.abort(); clearResult(); setStayAreaId(value); }} />
      </Form.Item>}
      <Form.Item label={`${label}名称`} required>
        <Input aria-label={`${label}名称`} value={name} maxLength={200} disabled={frozen}
          placeholder={`请输入${label}名称`} onChange={(value) => { clearResult(); setName(value); }}
          onPressEnter={() => void submit()} />
      </Form.Item>
    </Form>
    {searchError && <Alert type="error" content={searchError} />}
    {error && <Alert type="error" content={error} />}
    {result && <Alert type={result.outcome === "duplicate" ? "info" : "success"}
      content={result.outcome === "duplicate" ? `已存在同一地点，系统中的名称是：${result.name}`
        : `已添加：${result.name}。此地点尚未加入日程，请到目标日期打开“骨架编辑”，点击“添加点位”并搜索该名称。`} />}
  </Modal>;
}
